/**
 * Row-Level Security editor for one transaction form.
 *
 * Every rule is `<column> <op> <what its value source resolves to for the
 * current user>`, and ALL rules must match (AND) — so a record can be scoped
 * by company AND branch AND owner at once. The runtime engine filters lists,
 * 404s another user's record on load/edit/delete, STAMPS every `=` owner
 * column on add, and REFUSES a write whose value falls outside any other
 * operator's scope (see `_rls_rules` / `_rls_filters` / `_rls_stamp` /
 * `_rls_check_write` in `packages/runtime-engine/twasta_runtime_engine/
 * executor.py`, and `row_security_rules.py` for what each op means).
 *
 * `op` is = (default) | IN | LIKE | BETWEEN | > | >= | < | <=. The value
 * comes from a SOURCE, or from a read-only SQL query (`sql`) — e.g.
 * `SITE_CODE IN (SELECT site_code FROM user_site_rights WHERE user_id =
 * :user_id)`. IN takes every row of the query's first column; BETWEEN the
 * first row's two columns (or a second source, `source_to`).
 *
 * The source is `user_id` / `tenant_id`, a device built-in, or any declared
 * project variable — the engine resolves the variable for the logged-in user
 * (value / sql / login-prompt kinds alike). A source it cannot resolve owns
 * NOTHING, which is why an undeclared variable is surfaced here as an error
 * rather than left to silently return an empty list at run time.
 *
 * Written back in the legacy `{column, source}` shape while there is exactly
 * one rule (so older downloaded runtimes keep reading it) and as
 * `{rules: [...]}` from two rules up.
 *
 * `exempt_roles` names the roles the scope does not apply to. An
 * administrator scoped to their own slice cannot reconcile the master list,
 * and the screen that maps everyone else's scope is the one they are scoped
 * out of — so the roles that administer the object opt out of it. For them
 * the engine applies no filter, no ownership check AND no stamp; the last
 * one is what stops an admin's own owner value being written over another
 * scope's record on save.
 */

import { useState } from 'react'
import { AlertTriangle, Plus, Trash2 } from 'lucide-react'
import type {
  ColumnDef, RowSecurity, RowSecurityOp, RowSecurityRule, SqlModel,
} from './types'
import type { ProjectVariable } from '@/api/projectVariables'
import {
  RoleAdder, VariableInsert, useFunctionalRoles, type VariableToken,
} from '@/components/common/PolicyPickers'

/** The comparisons a rule may make — mirrors the engine's
 *  `row_security_rules.OPS` (+ the symbols it accepts as aliases). */
const OPS: { value: RowSecurityOp; label: string; title: string }[] = [
  { value: 'eq', label: '=', title: 'equals — the column is stamped on add' },
  { value: 'in', label: 'IN', title: 'is one of a list (e.g. the sites the user holds)' },
  { value: 'like', label: 'LIKE', title: 'matches a pattern (% = any run, _ = one character)' },
  { value: 'between', label: 'BETWEEN', title: 'within a low..high range (inclusive)' },
  { value: 'gt', label: '>', title: 'greater than' },
  { value: 'gte', label: '>=', title: 'at least' },
  { value: 'lt', label: '<', title: 'less than' },
  { value: 'lte', label: '<=', title: 'at most' },
]

const OP_VALUES = new Set<string>(OPS.map(o => o.value))
const OP_ALIASES: Record<string, RowSecurityOp> = {
  '': 'eq', '=': 'eq', '==': 'eq', equals: 'eq',
  '>': 'gt', '>=': 'gte', '<': 'lt', '<=': 'lte',
}

/** Canonical op for an authored spelling; unknown spellings are kept verbatim
 *  so the editor can flag them (the engine matches nothing for one). */
function normalizeOp(raw: unknown): string {
  const s = String(raw ?? '').trim().toLowerCase()
  if (OP_VALUES.has(s)) return s
  return OP_ALIASES[s] ?? s
}

/** Tokens the engine binds inside a rule's SQL (`_rls_identity_binds` +
 *  `IDENTITY_TOKEN_ALIASES`); project variables are appended per project. */
const RULE_SQL_TOKENS: VariableToken[] = [
  { token: ':user_id', label: 'Signed-in user id' },
  { token: ':short_user_id', label: "Signed-in user's 10-character id (audit form)" },
  { token: ':user_email', label: 'Signed-in user email' },
  { token: ':tenant_id', label: "Signed-in user's tenant" },
  { token: ':project', label: 'This application' },
]

/** Same guard as the engine (`is_safe_select`): one SELECT / CTE, no `;`,
 *  no comments. A query failing it matches NO rows at run time. */
function sqlProblem(sql: string): string | null {
  const s = sql.trim().replace(/;\s*$/, '').trim()
  if (!s) return 'Enter the query that returns the allowed value(s).'
  if (/;|--|\/\*|\*\//.test(s)) {
    return 'Use a single statement with no ";" and no comments.'
  }
  if (!/^(select|with)\b/i.test(s)) return 'The query must be a SELECT.'
  return null
}

// Identity sources the engine reserves — a project variable cannot shadow
// them (mirrors `_rls_owner_value`).
const IDENTITY_SOURCES: { value: string; label: string }[] = [
  { value: 'user_id', label: 'The signed-in user (user_id)' },
  {
    value: 'short_user_id',
    label: "The signed-in user's 10-character id (short_user_id — as in ADD_USER / CHG_USER)",
  },
  { value: 'tenant_id', label: "The user's tenant (tenant_id)" },
]

// Built-in variables the engine merges in for handheld / offline apps. A
// project may declare a variable of the same name to override either.
const DEVICE_SOURCES: { value: string; label: string }[] = [
  { value: ':device_code', label: "This device's registered code (:device_code)" },
  { value: ':device_user', label: 'The device operator (:device_user)' },
]

/** Width each identity source writes: 36-character user/tenant ids, the
 *  10-character short user id (the audit-column form). */
const IDENTITY_WIDTHS: Record<string, number> = {
  user_id: 36, tenant_id: 36, short_user_id: 10,
}

/** The built-in administrator roles, offered first because they are what
 *  "administrators see everything" almost always means. `admin` is also held
 *  automatically by platform administrator accounts (the engine matches the
 *  same way `is_super_approver` does). */
const ADMIN_ROLES = ['admin', 'func-admin']

/** Canonical `:name` for a variable source; identity keywords pass through. */
function normalizeSource(raw: string): string {
  const s = (raw || '').trim()
  return s || 'user_id'
}

function isIdentitySource(source: string): boolean {
  const s = (source || '').trim().toLowerCase()
  return s in IDENTITY_WIDTHS
}

/** The variable name a non-identity source names (`:branch` → `branch`). */
export function sourceVariableName(source: string): string | null {
  const s = (source || '').trim()
  if (!s || isIdentitySource(s)) return null
  return (s.startsWith(':') ? s.slice(1) : s).trim().toLowerCase() || null
}

/**
 * Read either authored shape as a rule list — the legacy single object or the
 * `rules` array. Mirrors the engine's `_rls_rules`, including dropping blank
 * columns so the editor never shows a rule the runtime would ignore.
 */
export function readRules(rs: RowSecurity | undefined): RowSecurityRule[] {
  if (!rs || typeof rs !== 'object') return []
  const entries: unknown[] = Array.isArray(rs.rules)
    ? rs.rules
    : (rs.column ? [rs] : [])
  const out: RowSecurityRule[] = []
  const seen = new Set<string>()
  for (const e of entries) {
    if (!e || typeof e !== 'object') continue
    const column = String((e as RowSecurityRule).column || '').trim()
    if (!column || seen.has(column.toLowerCase())) continue
    seen.add(column.toLowerCase())
    const r = e as RowSecurityRule
    const rule: RowSecurityRule = { column, source: normalizeSource(r.source || '') }
    const op = normalizeOp(r.op)
    if (op !== 'eq') rule.op = op as RowSecurityOp
    // A present `sql` key — even blank — means SQL mode (the engine treats a
    // blank query as "no value yet": matches nothing).
    if (typeof r.sql === 'string') rule.sql = r.sql
    if (typeof r.source_to === 'string' && r.source_to.trim()) rule.source_to = r.source_to.trim()
    out.push(rule)
  }
  return out
}

/** One rule as written to the model — `op` only when not `=`, and only the
 *  value fields the rule's op + mode use, so a plain equality rule is written
 *  exactly as it always was. */
function serializeRule(r: RowSecurityRule): RowSecurityRule {
  const op = normalizeOp(r.op)
  const out: RowSecurityRule = { column: r.column }
  if (typeof r.sql === 'string') {
    // SQL mode — kept even while blank so the editor stays in SQL mode; the
    // engine treats a blank query as "no value" (matches nothing).
    out.sql = r.sql
  } else {
    out.source = normalizeSource(r.source || '')
    if (op === 'between') out.source_to = (r.source_to || '').trim()
  }
  if (op !== 'eq') out.op = op as RowSecurityOp
  return out
}

export function readExemptRoles(rs: RowSecurity | undefined): string[] {
  const raw = rs && typeof rs === 'object' ? rs.exempt_roles : undefined
  const list = Array.isArray(raw) ? raw : (typeof raw === 'string' ? [raw] : [])
  const out: string[] = []
  for (const r of list) {
    const v = String(r || '').trim().toLowerCase()
    if (v && !out.includes(v)) out.push(v)
  }
  return out
}

/**
 * Write rules back in the narrowest shape that expresses them: nothing at all
 * when empty, the legacy single object for one rule (so a runtime built
 * before multi-rule support still reads it), the list from two up.
 *
 * `exempt` rides along on either shape, and is omitted when empty so a model
 * that never used it is written exactly as it was.
 */
export function writeRules(rules: RowSecurityRule[],
                           exempt: string[] = []): RowSecurity | undefined {
  const clean = rules.filter(r => (r.column || '').trim())
  if (clean.length === 0) return undefined
  const roles = exempt.filter(Boolean)
  const tail = roles.length ? { exempt_roles: roles } : {}
  if (clean.length === 1) {
    return { ...serializeRule(clean[0]), ...tail }
  }
  return { rules: clean.map(serializeRule), ...tail }
}

/** Declared width of a CHAR/VARCHAR column, or null when it isn't sized. */
function columnWidth(col: ColumnDef | undefined): number | null {
  if (!col) return null
  const size = parseInt(String(col.db_size || '').trim(), 10)
  if (Number.isFinite(size) && size > 0) return size
  // `col_type` may carry the size inline (CHAR3, VARCHAR50).
  const m = String(col.col_type || '').trim().match(/^[A-Z]+(\d+)/i)
  return m ? parseInt(m[1], 10) : null
}

function isTextColumn(col: ColumnDef | undefined): boolean {
  const t = String(col?.col_type || '').trim().toUpperCase()
  return t.startsWith('CHAR') || t.startsWith('VARCHAR')
}

function toBool(v: boolean | string | number | undefined): boolean {
  return v === true || v === 'true' || v === '1' || v === 1
}

export interface RowSecurityEditorProps {
  form: SqlModel
  /** Main-table columns only — the engine can only scope by those. */
  columns: ColumnDef[]
  projectVars: ProjectVariable[]
  /** Project id — lists the functional roles offered as exemptions. */
  project?: string
  /** True for a non-header form (child rows inherit the parent's scope). */
  isChildForm?: boolean
  onChange: (next: RowSecurity | undefined) => void
  /** Set `hidden` + `protect` on an owner column left editable on the form. */
  onHideAndProtect: (dbName: string) => void
}

const cardStyle: React.CSSProperties = {
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  padding: 8,
  marginBottom: 8,
  background: 'var(--color-bg)',
}

const noteStyle: React.CSSProperties = {
  fontSize: '0.8125rem',
  color: 'var(--color-text-secondary)',
  marginBottom: 6,
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '7px 10px',
  background: 'var(--color-input-bg)',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 6,
  color: 'var(--color-text)',
  fontSize: '0.9375rem', minHeight: 36, boxSizing: 'border-box',
}

const addBtnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,
  padding: '4px 8px',
  fontSize: '0.8125rem',
  color: 'var(--color-accent)',
  background: 'none',
  border: '1px dashed var(--color-border)',
  borderRadius: 4,
  cursor: 'pointer',
  width: '100%',
  marginTop: 4,
}

function Advisory({ tone, children }: {
  tone: 'warn' | 'error'
  children: React.ReactNode
}) {
  const color = tone === 'error' ? '#c62828' : '#b26a00'
  const bg = tone === 'error' ? 'rgba(198,40,40,0.10)' : 'rgba(255,152,0,0.10)'
  const border = tone === 'error' ? 'rgba(198,40,40,0.35)' : 'rgba(255,152,0,0.35)'
  return (
    <div style={{
      display: 'flex', gap: 6, alignItems: 'flex-start',
      fontSize: '0.8125rem', color, background: bg,
      border: `1px solid ${border}`, borderRadius: 4,
      padding: '5px 7px', marginTop: 6,
    }}>
      <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
      <div>{children}</div>
    </div>
  )
}

/** The source picker shared by a rule's value and a BETWEEN's upper bound. */
function SourceSelect({ value, projectVars, declared, varName, onChange, style }: {
  value: string
  projectVars: ProjectVariable[]
  declared: boolean
  varName: string | null
  onChange: (v: string) => void
  style?: React.CSSProperties
}) {
  return (
    <select style={{ ...inputStyle, ...style }} value={value}
      onChange={e => onChange(e.target.value)}>
      {!value && <option value="">— pick a source —</option>}
      {!declared && varName && (
        <option value={value}>{`:${varName} — not declared`}</option>
      )}
      <optgroup label="Identity">
        {IDENTITY_SOURCES.map(s => (
          <option key={s.value} value={s.value}>{s.label}</option>
        ))}
      </optgroup>
      <optgroup label="Device">
        {DEVICE_SOURCES.map(s => (
          <option key={s.value} value={s.value}>{s.label}</option>
        ))}
      </optgroup>
      {projectVars.length > 0 && (
        <optgroup label="Project variables">
          {projectVars.map(v => {
            const token = `:${v.name.trim().toLowerCase()}`
            const desc = v.description
              || (v.kind === 'prompt' ? 'asked at login'
                : v.kind === 'sql' ? 'SQL query' : 'fixed value')
            return (
              <option key={v.name} value={token}>
                {`${token} — ${desc} (${v.kind})`}
              </option>
            )
          })}
        </optgroup>
      )}
    </select>
  )
}

const modeBtnStyle = (active: boolean): React.CSSProperties => ({
  padding: '2px 8px',
  fontSize: '0.8125rem',
  border: '1px solid var(--color-border)',
  background: active ? 'var(--color-accent)' : 'var(--color-bg)',
  color: active ? '#fff' : 'var(--color-text-secondary)',
  cursor: 'pointer',
})

export default function RowSecurityEditor({
  form, columns, projectVars, project = '', isChildForm = false,
  onChange, onHideAndProtect,
}: RowSecurityEditorProps) {
  const rules = readRules(form.row_security)
  const exempt = readExemptRoles(form.row_security)
  const functionalRoles = useFunctionalRoles(project)
  const [roleDraft, setRoleDraft] = useState('')
  const setRules = (next: RowSecurityRule[]) => onChange(writeRules(next, exempt))
  const setExempt = (next: string[]) => onChange(writeRules(rules, next))
  const toggleExempt = (role: string) => setExempt(
    exempt.includes(role) ? exempt.filter(r => r !== role) : [...exempt, role],
  )
  const addExempt = (raw: string) => {
    const role = raw.trim().toLowerCase()
    if (role && !exempt.includes(role)) setExempt([...exempt, role])
  }

  const colOf = (dbName: string): ColumnDef | undefined =>
    columns.find(c => (c.db_name || c.name) === dbName)

  const varByName = new Map(
    projectVars.map(v => [v.name.trim().toLowerCase(), v]),
  )
  const deviceNames = new Set(
    DEVICE_SOURCES.map(d => d.value.slice(1)),
  )
  const isDeclared = (name: string | null) =>
    name ? (varByName.has(name) || deviceNames.has(name)) : true

  const sqlTokens: VariableToken[] = [
    ...RULE_SQL_TOKENS,
    ...projectVars.filter(v => v.name).map(v => ({
      token: `:${v.name.trim().toLowerCase()}`,
      label: v.description ? `${v.description} (project variable)` : 'project variable',
    })),
  ]

  const patch = (idx: number, p: Partial<RowSecurityRule>) =>
    setRules(rules.map((r, i) => (i === idx ? { ...r, ...p } : r)))

  /** Switch a rule between a source and a SQL query (dropping the other). */
  const setMode = (idx: number, mode: 'source' | 'sql') =>
    setRules(rules.map((r, i) => {
      if (i !== idx) return r
      const base: RowSecurityRule = { column: r.column, ...(r.op ? { op: r.op } : {}) }
      return mode === 'sql' ? { ...base, sql: '' } : { ...base, source: 'user_id' }
    }))

  const remove = (idx: number) => setRules(rules.filter((_, i) => i !== idx))

  const used = new Set(rules.map(r => r.column.toLowerCase()))
  const available = columns.filter(
    c => !used.has((c.db_name || c.name).toLowerCase()),
  )

  const addRule = () => {
    const first = available[0]
    if (!first) return
    setRules([...rules, {
      column: first.db_name || first.name, source: 'user_id',
    }])
  }

  const extraRoles = exempt.filter(r => !ADMIN_ROLES.includes(r))

  return (
    <div>
      <div style={noteStyle}>
        Restrict this form&apos;s records to what the current user may see.
        Every rule must match (AND): lists are filtered, a record outside the
        scope is hidden, <code>=</code> columns are stamped on add, and a value
        outside any other rule&apos;s scope is refused on save.
      </div>

      {isChildForm && rules.length === 0 && (
        <div style={{ ...noteStyle, fontStyle: 'italic' }}>
          Child rows are already reached through their protected parent — a
          rule here is rarely needed.
        </div>
      )}

      {rules.length === 0 && (
        <div style={{
          fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
          fontStyle: 'italic', marginBottom: 6,
        }}>
          Off — all rows are visible to every user.
        </div>
      )}

      {rules.map((rule, idx) => {
        const col = colOf(rule.column)
        const op = normalizeOp(rule.op)
        const knownOp = OP_VALUES.has(op)
        const sqlMode = typeof rule.sql === 'string'
        const source = normalizeSource(rule.source || '')
        const varName = sqlMode ? null : sourceVariableName(source)
        const declared = isDeclared(varName)
        const toSource = (rule.source_to || '').trim()
        const toVarName = toSource ? sourceVariableName(toSource) : null
        const toDeclared = isDeclared(toVarName)
        const sqlIssue = sqlMode ? sqlProblem(rule.sql || '') : null

        // The column is gone from the form (renamed / removed) — the engine
        // would filter on a column that isn't there.
        const missingColumn = !col

        // Identity ids are 36 chars; a narrower owner column truncates or
        // errors on insert. Only an `=` rule writes the source into it.
        const width = columnWidth(col)
        const identityWidth = IDENTITY_WIDTHS[source.toLowerCase()] ?? 0
        const tooNarrow = op === 'eq' && !sqlMode && isIdentitySource(source)
          && isTextColumn(col) && width !== null && width < identityWidth

        // An `=` column is stamped by the engine — an end user editing one
        // would be handing their record to somebody else. Any other op is
        // CHOSEN by the user (their site, a value in range) and checked.
        const editable = !!col && !(toBool(col.hidden) || toBool(col.protect))

        return (
          <div key={idx} style={cardStyle}>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <select
                style={{ ...inputStyle, flex: 1 }}
                value={rule.column}
                onChange={e => patch(idx, { column: e.target.value })}
              >
                {missingColumn && (
                  <option value={rule.column}>
                    {rule.column} — not on this form
                  </option>
                )}
                {columns
                  .filter(c => {
                    const dbn = (c.db_name || c.name).toLowerCase()
                    return dbn === rule.column.toLowerCase() || !used.has(dbn)
                  })
                  .map((c, i) => {
                    const dbn = c.db_name || c.name
                    return <option key={i} value={dbn}>{dbn}</option>
                  })}
              </select>
              <select
                style={{ ...inputStyle, width: 92, flex: 'none' }}
                value={op}
                title={OPS.find(o => o.value === op)?.title || 'Unknown operator'}
                onChange={e => {
                  const next = e.target.value as RowSecurityOp
                  patch(idx, {
                    op: next === 'eq' ? undefined : next,
                    source_to: next === 'between' ? rule.source_to : undefined,
                  })
                }}
              >
                {!knownOp && <option value={op}>{`${op} — unknown`}</option>}
                {OPS.map(o => (
                  <option key={o.value} value={o.value} title={o.title}>{o.label}</option>
                ))}
              </select>
              <button
                className="btn btn-ghost btn-sm"
                title="Remove rule"
                onClick={() => remove(idx)}
              >
                <Trash2 size={14} />
              </button>
            </div>

            <div style={{
              display: 'flex', alignItems: 'center', gap: 6, marginTop: 6,
            }}>
              <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                Value from
              </span>
              <div style={{ display: 'inline-flex', borderRadius: 4, overflow: 'hidden' }}>
                <button type="button" style={modeBtnStyle(!sqlMode)}
                  onClick={() => sqlMode && setMode(idx, 'source')}>
                  Source
                </button>
                <button type="button" style={modeBtnStyle(sqlMode)}
                  title="A read-only query run for the signed-in user"
                  onClick={() => !sqlMode && setMode(idx, 'sql')}>
                  SQL query
                </button>
              </div>
            </div>

            {!sqlMode && (
              <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginTop: 6 }}>
                <SourceSelect
                  value={source} projectVars={projectVars}
                  declared={declared} varName={varName}
                  onChange={v => patch(idx, { source: v })}
                  style={{ flex: 1 }}
                />
                {op === 'between' && (
                  <>
                    <span style={{
                      fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
                    }}>and</span>
                    <SourceSelect
                      value={toSource} projectVars={projectVars}
                      declared={toDeclared} varName={toVarName}
                      onChange={v => patch(idx, { source_to: v })}
                      style={{ flex: 1 }}
                    />
                  </>
                )}
              </div>
            )}

            {sqlMode && (
              <div style={{ marginTop: 6 }}>
                <textarea
                  style={{
                    ...inputStyle, minHeight: 64, resize: 'vertical',
                    fontFamily: 'var(--font-mono, monospace)', fontSize: '0.8425rem',
                  }}
                  value={rule.sql || ''}
                  placeholder={op === 'between'
                    ? 'SELECT min_amount, max_amount FROM user_limits WHERE user_id = :user_id'
                    : 'SELECT site_code FROM user_site_rights WHERE user_id = :user_id'}
                  onChange={e => patch(idx, { sql: e.target.value })}
                />
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', gap: 6, marginTop: 4,
                }}>
                  <div style={{ ...noteStyle, marginBottom: 0 }}>
                    {op === 'in' && 'Every row’s first column is an allowed value.'}
                    {op === 'between' && 'The first row’s two columns are the low and high bounds.'}
                    {op !== 'in' && op !== 'between' && 'The first row’s first column is the value.'}
                  </div>
                  <VariableInsert
                    tokens={sqlTokens}
                    onInsert={t => patch(idx, {
                      sql: `${(rule.sql || '').replace(/\s*$/, '')} ${t}`.trimStart(),
                    })}
                  />
                </div>
              </div>
            )}

            {!knownOp && (
              <Advisory tone="error">
                <code>{op}</code> is not an operator the engine knows, so this
                rule matches <strong>no rows</strong>. Pick one from the list.
              </Advisory>
            )}

            {missingColumn && (
              <Advisory tone="error">
                <strong>{rule.column}</strong> is not a column on this form.
                The rule will filter on a column the engine can&apos;t find —
                pick an existing column or remove the rule.
              </Advisory>
            )}

            {!declared && varName && (
              <Advisory tone="error">
                <code>:{varName}</code> is not a declared project variable, so
                this rule matches <strong>no rows</strong> (the engine scopes
                the user out rather than guessing). Declare it under{' '}
                <strong>Workbench → Project Variables</strong>.
              </Advisory>
            )}

            {op === 'between' && !sqlMode && (!toSource || (!toDeclared && toVarName)) && (
              <Advisory tone="error">
                {toSource
                  ? <><code>:{toVarName}</code> is not a declared project variable.</>
                  : 'Pick the upper bound’s source.'}{' '}
                Until it resolves, this rule matches <strong>no rows</strong>.
              </Advisory>
            )}

            {sqlIssue && (
              <Advisory tone="error">
                {sqlIssue} Until then this rule matches <strong>no rows</strong>.
              </Advisory>
            )}

            {tooNarrow && (
              <Advisory tone="warn">
                {source === 'tenant_id' ? 'Tenant ids' : source === 'short_user_id'
                  ? 'Short user ids' : 'User ids'} are{' '}
                {identityWidth} characters, but <strong>{rule.column}</strong>{' '}
                holds {width}.{' '}
                {source === 'user_id'
                  ? 'Widen the column, or use the 10-character short_user_id instead.'
                  : 'Widen the column, or scope by a project variable instead.'}
              </Advisory>
            )}

            {op === 'eq' && !sqlMode && editable && (
              <Advisory tone="warn">
                <strong>{rule.column}</strong> is editable on the form. Owner
                columns are stamped by the engine and should not be shown.{' '}
                <button
                  type="button"
                  onClick={() => onHideAndProtect(rule.column)}
                  style={{
                    background: 'none', border: 'none', padding: 0,
                    color: 'inherit', textDecoration: 'underline',
                    cursor: 'pointer', font: 'inherit',
                  }}
                >
                  Hide &amp; protect it
                </button>
              </Advisory>
            )}

            {knownOp && (op !== 'eq' || sqlMode) && (
              <div style={{ ...noteStyle, marginTop: 6, marginBottom: 0 }}>
                {op === 'eq'
                  ? <>The query&apos;s value is stamped into <strong>{rule.column}</strong> on add.</>
                  : <>Users enter <strong>{rule.column}</strong> themselves; a
                    value outside their scope is refused on save.
                    {op === 'in' && ' With exactly one allowed value it is filled in for them.'}</>}
              </div>
            )}
          </div>
        )
      })}

      <button
        style={{
          ...addBtnStyle,
          opacity: available.length === 0 ? 0.5 : 1,
          cursor: available.length === 0 ? 'not-allowed' : 'pointer',
        }}
        disabled={available.length === 0}
        title={available.length === 0
          ? 'Every main-table column is already used by a rule'
          : 'Add another condition — all rules must match'}
        onClick={addRule}
      >
        <Plus size={12} /> Add rule
      </button>

      {rules.length > 1 && (
        <div style={{ ...noteStyle, marginTop: 6, marginBottom: 0 }}>
          A record is visible only when <strong>all {rules.length}</strong>{' '}
          rules match.
        </div>
      )}

      {rules.length > 0 && (
        <div style={{ ...cardStyle, marginTop: 8, marginBottom: 0 }}>
          <div style={{
            fontSize: '0.8125rem', fontWeight: 600, marginBottom: 4,
          }}>
            Roles that see every record
          </div>
          <div style={{ ...noteStyle, marginBottom: 6 }}>
            These roles are exempt: no filter, no check, and their edits never
            re-stamp a record&apos;s owner. A user holding ANY one of them is
            exempt. Leave all of them off and the scope applies to everybody,
            including administrators.
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {[...ADMIN_ROLES, ...extraRoles].map(role => (
              <label key={role} style={{
                display: 'flex', alignItems: 'center', gap: 4,
                fontSize: '0.8125rem', cursor: 'pointer',
              }}>
                <input
                  type="checkbox"
                  checked={exempt.includes(role)}
                  onChange={() => toggleExempt(role)}
                />
                <code>{role}</code>
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8 }}>
            <RoleAdder
              roles={functionalRoles.map(r => r.trim().toLowerCase())}
              current={[...ADMIN_ROLES, ...exempt]}
              onAdd={addExempt}
            />
            <input
              style={{ ...inputStyle, flex: 1, fontSize: '0.8125rem', padding: '4px 8px' }}
              value={roleDraft}
              placeholder="or type a role name and press Enter"
              onChange={e => setRoleDraft(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  addExempt(roleDraft)
                  setRoleDraft('')
                }
              }}
            />
          </div>
          {exempt.length > 0 && (
            <Advisory tone="warn">
              A record this form holds is visible in full to{' '}
              <strong>{exempt.join(', ')}</strong>. Check that is right for
              personal or otherwise restricted data.
            </Advisory>
          )}
        </div>
      )}
    </div>
  )
}
